import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Reservation, Member, ReservationRvInfo } from '../../types';
import {
  getReservations,
  mapApiToGridReservations,
  attachRvInfoToReservations,
  undoCheckIn,
} from '../../services/reservationService';
import { getMemberById, mapApiMember, getRecreationalVehicles } from '../../services/memberService';
import { fetchPendingManualPayments } from '../../services/paymentService';
import { authService } from '../../services/auth';
import { getTodayString, addDays } from '../../utils/dateUtils';
import LoadingSpinner from '../../utils/LoadingSpinner';
import ReservationPopup from '../reservations/ReservationPopup';
import PaymentPopup from '../reservations/PaymentPopup';
import CheckInOutModal from '../reservations/CheckInOutModal';
import ModifyReservationWizard from '../reservations/ModifyReservationWizard';
import AdminCancellationWizard from '../reservations/CancellationWizard/AdminCancellationWizard';
import ResendAgreementModal from '../reservations/ResendAgreementModal';
import DailyReservationRow from './DailyReservationRow';
import './DailyReport.css';

const DailyReport: React.FC = () => {
  const navigate = useNavigate();
  const [date, setDate] = useState(getTodayString);
  const [arrivals, setArrivals] = useState<Reservation[]>([]);
  const [departures, setDepartures] = useState<Reservation[]>([]);
  const [upcomingArrivals, setUpcomingArrivals] = useState<{ date: string; reservations: Reservation[] }[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentDueIds, setPaymentDueIds] = useState<Set<number>>(new Set());

  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [selectedMember, setSelectedMember] = useState<Member | null>(null);
  const [selectedSiteId, setSelectedSiteId] = useState<number | undefined>(undefined);
  const [selectedSiteAlias, setSelectedSiteAlias] = useState<string | undefined>(undefined);
  const [showPopup, setShowPopup] = useState(false);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [showCheckInModal, setShowCheckInModal] = useState(false);
  const [showCheckOutModal, setShowCheckOutModal] = useState(false);
  const [showModifyWizard, setShowModifyWizard] = useState(false);
  const [showCancelWizard, setShowCancelWizard] = useState(false);
  const [showResendModal, setShowResendModal] = useState(false);

  const loadData = useCallback(async (selectedDate: string) => {
    setLoading(true);
    setError(null);
    try {
      const storeId = authService.getStoreId();
      const [arrivalsApi, departuresApi, upcomingApi, pendingPayments] = await Promise.all([
        getReservations({ checkInFrom: selectedDate, checkInTo: selectedDate, activeOnly: true }),
        getReservations({ checkInFrom: addDays(selectedDate, -60), checkInTo: selectedDate, activeOnly: true }),
        getReservations({ checkInFrom: addDays(selectedDate, 1), checkInTo: addDays(selectedDate, 7), activeOnly: true }),
        storeId
          ? fetchPendingManualPayments(storeId).catch(err => {
              console.error('Pending payments fetch error:', err);
              return [];
            })
          : Promise.resolve([]),
      ]);

      setPaymentDueIds(new Set(
        pendingPayments
          .map(p => Number(p.reservation_id))
          .filter(id => !Number.isNaN(id))
      ));

      const allArrivals = mapApiToGridReservations(arrivalsApi)
        .filter(r => r.status === 'confirmed' || r.status === 'checked_in' || r.status === 'checked_out');
      const allDepartures = mapApiToGridReservations(departuresApi)
        .filter(r => r.checkOut === selectedDate && (r.status === 'confirmed' || r.status === 'checked_in' || r.status === 'checked_out'));
      const allUpcomingMapped = mapApiToGridReservations(upcomingApi)
        .filter(r => r.status === 'confirmed' || r.status === 'checked_in' || r.status === 'checked_out');

      // Fetch RV data for all unique member IDs that have vehicles
      const memberIdsWithVehicles = new Set<number>();
      for (const r of [...allArrivals, ...allDepartures, ...allUpcomingMapped]) {
        if (r.memberId != null && r.vehicles > 0) memberIdsWithVehicles.add(r.memberId);
      }
      const rvMap = new Map<number, ReservationRvInfo[]>();
      if (memberIdsWithVehicles.size > 0) {
        const rvResults = await Promise.all(
          Array.from(memberIdsWithVehicles).map(async memberId => ({
            memberId,
            rvs: await getRecreationalVehicles(memberId),
          }))
        );
        for (const { memberId, rvs } of rvResults) {
          rvMap.set(memberId, rvs.filter(rv => rv.active).map(rv => ({
            type: rv.type,
            description: rv.description,
            length: rv.length,
            amp: rv.amp,
            licensePlate: rv.license_plate,
          })));
        }
      }

      setArrivals(
        Array.from(new Map(attachRvInfoToReservations(allArrivals, rvMap).map(r => [`${r.id}-${r.siteId}`, r])).values())
          .sort((a, b) => a.siteAlias.localeCompare(b.siteAlias, undefined, { numeric: true }))
      );

      setDepartures(
        Array.from(new Map(attachRvInfoToReservations(allDepartures, rvMap).map(r => [`${r.id}-${r.siteId}`, r])).values())
          .sort((a, b) => a.siteAlias.localeCompare(b.siteAlias, undefined, { numeric: true }))
      );

      const uniqueUpcoming = Array.from(
        new Map(
          attachRvInfoToReservations(allUpcomingMapped, rvMap).map(r => [`${r.id}-${r.siteId}`, r])
        ).values()
      );
      const upcomingByDate = new Map<string, Reservation[]>();
      for (const r of uniqueUpcoming) {
        if (!upcomingByDate.has(r.checkIn)) upcomingByDate.set(r.checkIn, []);
        upcomingByDate.get(r.checkIn)!.push(r);
      }
      setUpcomingArrivals(
        Array.from(upcomingByDate.entries())
          .sort(([a], [b]) => a.localeCompare(b))
          .map(([d, reservations]) => ({
            date: d,
            reservations: reservations.sort((a, b) =>
              a.siteAlias.localeCompare(b.siteAlias, undefined, { numeric: true })
            ),
          }))
      );
    } catch (err: any) {
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData(date);
  }, [date, loadData]);

  const hasPaymentDue = (r: Reservation): boolean =>
    paymentDueIds.has(r.id) ||
    (r.originalReservationId != null && paymentDueIds.has(r.originalReservationId));

  const handleRowClick = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setSelectedMember(null);
    setShowPopup(true);
    if (reservation.memberId) {
      getMemberById(reservation.memberId)
        .then(api => setSelectedMember(mapApiMember(api)))
        .catch(err => console.error('Failed to load member:', err));
    }
  };

  const handleRowCheckIn = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setSelectedSiteId(reservation.siteId || undefined);
    setSelectedSiteAlias(reservation.siteAlias || undefined);
    setShowCheckInModal(true);
  };

  const handleRowCheckOut = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setSelectedSiteId(reservation.siteId || undefined);
    setSelectedSiteAlias(reservation.siteAlias || undefined);
    setShowCheckOutModal(true);
  };

  const handleRowModify = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setSelectedSiteId(reservation.siteId || undefined);
    setSelectedSiteAlias(reservation.siteAlias || undefined);
    setShowModifyWizard(true);
  };

  const closeAllModals = useCallback(() => {
    setShowPopup(false);
    setShowPaymentPopup(false);
    setShowCheckInModal(false);
    setShowCheckOutModal(false);
    setShowModifyWizard(false);
    setShowCancelWizard(false);
    setShowResendModal(false);
    setSelectedReservation(null);
    setSelectedMember(null);
    setSelectedSiteId(undefined);
    setSelectedSiteAlias(undefined);
  }, []);

  const handleActionSuccess = useCallback(() => {
    closeAllModals();
    loadData(date);
  }, [closeAllModals, loadData, date]);

  const handleUndoCheckIn = async (index: number) => {
    if (!selectedReservation) return;
    try {
      await undoCheckIn(selectedReservation.id, selectedReservation.specialRequests, index);
      closeAllModals();
      loadData(date);
    } catch (err) {
      console.error('Failed to undo check-in:', err);
    }
  };

  return (
    <div className="daily-report">
      <div className="daily-report__header">
        <h1 className="daily-report__title">Daily Report</h1>
        <div className="daily-report__date-nav">
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setDate(d => addDays(d, -1))}
          >
            ←
          </button>
          <input
            type="date"
            className="daily-report__date-input"
            value={date}
            onChange={e => e.target.value && setDate(e.target.value)}
          />
          <button
            className="btn btn-secondary btn-sm"
            onClick={() => setDate(d => addDays(d, 1))}
          >
            →
          </button>
        </div>
      </div>

      {error && <div className="daily-report__error">{error}</div>}

      {loading ? (
        <LoadingSpinner />
      ) : (
        <>
          <div className="daily-report__panels">
            <div className="daily-panel">
              <div className="daily-panel__header">
                <h2 className="daily-panel__title">Arrivals</h2>
                <span className={`daily-panel__count${arrivals.length === 0 ? ' daily-panel__count--zero' : ''}`}>
                  {arrivals.length}
                </span>
              </div>
              <div className="daily-panel__list">
                {arrivals.length === 0 ? (
                  <p className="daily-panel__empty">No arrivals for this date.</p>
                ) : (
                  arrivals.map(r => (
                    <DailyReservationRow
                      key={`${r.id}-${r.siteId}`}
                      reservation={r}
                      panelType="arrival"
                      hasPaymentDue={hasPaymentDue(r)}
                      onClick={() => handleRowClick(r)}
                      onCheckIn={() => handleRowCheckIn(r)}
                      onCheckOut={() => handleRowCheckOut(r)}
                      onModify={() => handleRowModify(r)}
                    />
                  ))
                )}
              </div>
            </div>

            <div className="daily-panel">
              <div className="daily-panel__header">
                <h2 className="daily-panel__title">Departures</h2>
                <span className={`daily-panel__count${departures.length === 0 ? ' daily-panel__count--zero' : ''}`}>
                  {departures.length}
                </span>
              </div>
              <div className="daily-panel__list">
                {departures.length === 0 ? (
                  <p className="daily-panel__empty">No departures for this date.</p>
                ) : (
                  departures.map(r => (
                    <DailyReservationRow
                      key={`${r.id}-${r.siteId}`}
                      reservation={r}
                      panelType="departure"
                      hasPaymentDue={hasPaymentDue(r)}
                      onClick={() => handleRowClick(r)}
                      onCheckIn={() => handleRowCheckIn(r)}
                      onCheckOut={() => handleRowCheckOut(r)}
                      onModify={() => handleRowModify(r)}
                    />
                  ))
                )}
              </div>
            </div>
          </div>

          <div className="upcoming-section">
            <div className="upcoming-section__header">
              <h2 className="upcoming-section__title">Upcoming Check-ins</h2>
              <span className="upcoming-section__subtitle">Next 7 days</span>
              {upcomingArrivals.length > 0 && (
                <span className="upcoming-section__total">
                  {upcomingArrivals.reduce((sum, g) => sum + g.reservations.length, 0)}
                </span>
              )}
            </div>
            {upcomingArrivals.length === 0 ? (
              <p className="daily-panel__empty">No arrivals in the next 7 days.</p>
            ) : (
              upcomingArrivals.map(({ date: d, reservations }) => (
                <div key={d} className="upcoming-day">
                  <div className="upcoming-day__header">
                    <span className="upcoming-day__label">
                      {new Date(d + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}
                    </span>
                    <span className="upcoming-day__count">{reservations.length}</span>
                  </div>
                  {reservations.map(r => (
                    <DailyReservationRow
                      key={`${r.id}-${r.siteId}`}
                      reservation={r}
                      panelType="arrival"
                      showActions={false}
                      hasPaymentDue={hasPaymentDue(r)}
                      onClick={() => handleRowClick(r)}
                      onCheckIn={() => handleRowCheckIn(r)}
                      onCheckOut={() => handleRowCheckOut(r)}
                      onModify={() => handleRowModify(r)}
                    />
                  ))}
                </div>
              ))
            )}
          </div>
        </>
      )}

      {showPopup && selectedReservation && (
        <ReservationPopup
          reservation={selectedReservation}
          member={selectedMember}
          onClose={() => { setShowPopup(false); setSelectedReservation(null); setSelectedMember(null); }}
          onViewMember={(memberId) => { closeAllModals(); navigate(`/members/${memberId}`); }}
          onViewPayment={() => setShowPaymentPopup(true)}
          onModify={() => setShowModifyWizard(true)}
          onCancel={() => setShowCancelWizard(true)}
          onResendAgreement={() => setShowResendModal(true)}
          onCheckIn={() => setShowCheckInModal(true)}
          onCheckOut={() => setShowCheckOutModal(true)}
          onUndoCheckIn={handleUndoCheckIn}
        />
      )}

      {showPaymentPopup && selectedReservation && (
        <PaymentPopup
          reservation={selectedReservation}
          onClose={() => setShowPaymentPopup(false)}
          onPaymentsChanged={() => loadData(date)}
        />
      )}

      {showCheckInModal && selectedReservation && (
        <CheckInOutModal
          reservation={selectedReservation}
          mode="checkin"
          siteId={selectedSiteId}
          siteAlias={selectedSiteAlias}
          onClose={() => setShowCheckInModal(false)}
          onSuccess={handleActionSuccess}
        />
      )}

      {showCheckOutModal && selectedReservation && (
        <CheckInOutModal
          reservation={selectedReservation}
          mode="checkout"
          siteId={selectedSiteId}
          siteAlias={selectedSiteAlias}
          onClose={() => setShowCheckOutModal(false)}
          onSuccess={handleActionSuccess}
        />
      )}

      {showModifyWizard && (
        <ModifyReservationWizard
          isOpen={showModifyWizard}
          reservation={selectedReservation}
          onClose={() => setShowModifyWizard(false)}
          onSuccess={handleActionSuccess}
        />
      )}

      {showCancelWizard && (
        <AdminCancellationWizard
          isOpen={showCancelWizard}
          reservation={selectedReservation}
          onClose={() => setShowCancelWizard(false)}
          onSuccess={handleActionSuccess}
        />
      )}

      {showResendModal && selectedReservation && (
        <ResendAgreementModal
          reservation={selectedReservation}
          onClose={() => setShowResendModal(false)}
          onSuccess={() => setShowResendModal(false)}
        />
      )}
    </div>
  );
};

export default DailyReport;
